import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown,
  Building,
  Car,
  PiggyBank,
  CreditCard,
  Home,
  GraduationCap,
  AlertCircle,
  ArrowRight,
  Calculator
} from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import CurrencySelector from './CurrencySelector';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  AreaChart,
  Area
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const NetWorthDisplayModule = ({ onNavigateToMyNumbers }) => {
  const { formatAmount } = useCurrency();
  const { t } = useTranslation();
  
  const [activeTab, setActiveTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [myNumbers, setMyNumbers] = useState(null);
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchSummary(),
        fetchMyNumbers(),
        fetchMonthlyHistory()
      ]);
    } catch (error) {
      console.error("Error fetching net worth data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/api/networth/summary`, {
        headers: getAuthHeaders()
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchMyNumbers = async () => {
    try {
      const response = await axios.get(`${API}/api/my-numbers`, {
        headers: getAuthHeaders()
      });
      setMyNumbers(response.data);
    } catch (error) {
      console.error('Error fetching my numbers:', error);
    }
  };

  const fetchMonthlyHistory = async () => {
    try {
      const response = await axios.get(`${API}/api/networth/monthly-history`, {
        headers: getAuthHeaders()
      });
      setMonthlyHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching monthly history:', error);
    }
  };

  // Calculate totals from My Numbers data
  const calculateFromMyNumbers = () => {
    if (!myNumbers) return { totalAssets: 0, totalDebt: 0, netWorth: 0 };
    
    const assets = myNumbers.assets || {};
    const debts = myNumbers.debts || {};
    
    const totalAssets = Object.values(assets).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const totalDebt = Object.values(debts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const netWorth = totalAssets - totalDebt;
    
    return { totalAssets, totalDebt, netWorth };
  };

  const { totalAssets, totalDebt, netWorth } = calculateFromMyNumbers();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading Net Worth...</p>
        </div>
      </div>
    );
  }

  // No data state - prompt to go to My Numbers
  if (!myNumbers || (totalAssets === 0 && totalDebt === 0)) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Net Worth</h2>
            <p className="text-gray-400">Your total personal wealth, net from debt</p>
          </div>
          <CurrencySelector />
        </div>
        
        <Card className="border-2 border-dashed border-emerald-300 bg-emerald-50">
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                <Calculator className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-emerald-800">Enter Your Financial Data</h3>
              <p className="text-emerald-700 max-w-md mx-auto">
                Your net worth is calculated from data entered in the <strong>My Numbers</strong> module. 
                Please fill in your assets and debts there to see your net worth analysis.
              </p>
              <Button 
                onClick={() => onNavigateToMyNumbers && onNavigateToMyNumbers()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Go to My Numbers
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-white">Net Worth</h2>
          <div className="flex items-center gap-4 flex-wrap mt-1">
            <p className="text-gray-400">Your total personal wealth, net from debt</p>
            <CurrencySelector />
          </div>
        </div>
        <Button 
          onClick={() => onNavigateToMyNumbers && onNavigateToMyNumbers()}
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-800"
        >
          <Calculator className="w-4 h-4 mr-2" />
          Edit in My Numbers
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-emerald-900/30 border-emerald-700">
          <CardHeader className="pb-2">
            <CardDescription className="text-emerald-300">Total Assets</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-400">
              {formatAmount(totalAssets)}
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-red-900/30 border-red-700">
          <CardHeader className="pb-2">
            <CardDescription className="text-red-300">Total Debt</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-400">
              {formatAmount(totalDebt)}
            </div>
          </CardContent>
        </Card>
        
        <Card className={`${netWorth >= 0 ? 'bg-blue-900/30 border-blue-700' : 'bg-orange-900/30 border-orange-700'}`}>
          <CardHeader className="pb-2">
            <CardDescription className={netWorth >= 0 ? 'text-blue-300' : 'text-orange-300'}>
              Net Worth
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${netWorth >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
              {formatAmount(netWorth)}
            </div>
            <div className="flex items-center mt-1">
              {netWorth >= 0 ? (
                <TrendingUp className="w-4 h-4 text-blue-400 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 text-orange-400 mr-1" />
              )}
              <span className={`text-sm ${netWorth >= 0 ? 'text-blue-300' : 'text-orange-300'}`}>
                {netWorth >= 0 ? 'Positive net worth' : 'Work towards positive'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap w-full justify-center md:justify-start gap-2 h-auto p-2 bg-gray-800/50 rounded-lg">
          <TabsTrigger value="overview" className="text-xs md:text-sm px-3 py-2 text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">
            Overview
          </TabsTrigger>
          <TabsTrigger value="assets" className="text-xs md:text-sm px-3 py-2 text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">
            Assets Breakdown
          </TabsTrigger>
          <TabsTrigger value="debts" className="text-xs md:text-sm px-3 py-2 text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">
            Debts Breakdown
          </TabsTrigger>
          <TabsTrigger value="history" className="text-xs md:text-sm px-3 py-2 text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">
            History
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Assets Summary */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-emerald-400" />
                  Assets Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {myNumbers?.assets && Object.entries(myNumbers.assets).map(([key, value]) => {
                    if (value === 0) return null;
                    const icons = {
                      savings: PiggyBank,
                      checking: Wallet,
                      cash: Wallet,
                      investments: TrendingUp,
                      retirement_accounts: Building,
                      real_estate: Home,
                      vehicles: Car,
                      other_assets: Wallet
                    };
                    const Icon = icons[key] || Wallet;
                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    return (
                      <div key={key} className="flex justify-between items-center p-2 bg-gray-900/50 rounded">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-emerald-400" />
                          <span className="text-gray-300">{label}</span>
                        </div>
                        <span className="text-emerald-400 font-medium">{formatAmount(value)}</span>
                      </div>
                    );
                  })}
                  {(!myNumbers?.assets || Object.values(myNumbers.assets).every(v => v === 0)) && (
                    <p className="text-gray-500 text-center py-4">No assets recorded</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Debts Summary */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-red-400" />
                  Debts Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {myNumbers?.debts && Object.entries(myNumbers.debts).map(([key, value]) => {
                    if (value === 0) return null;
                    const icons = {
                      credit_cards: CreditCard,
                      mortgage: Home,
                      car_loans: Car,
                      student_loans: GraduationCap,
                      personal_loans: Wallet,
                      other_debt: AlertCircle
                    };
                    const Icon = icons[key] || CreditCard;
                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    return (
                      <div key={key} className="flex justify-between items-center p-2 bg-gray-900/50 rounded">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-red-400" />
                          <span className="text-gray-300">{label}</span>
                        </div>
                        <span className="text-red-400 font-medium">{formatAmount(value)}</span>
                      </div>
                    );
                  })}
                  {(!myNumbers?.debts || Object.values(myNumbers.debts).every(v => v === 0)) && (
                    <p className="text-gray-500 text-center py-4">No debts recorded - Great!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Wealth Ratio */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Wealth Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-gray-900/50 rounded-lg">
                  <div className="text-3xl font-bold text-white">
                    {totalDebt > 0 ? (totalAssets / totalDebt).toFixed(2) : '∞'}
                  </div>
                  <div className="text-gray-400 text-sm mt-1">Asset to Debt Ratio</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {totalDebt === 0 ? 'Debt free!' : totalAssets >= totalDebt * 2 ? 'Healthy' : 'Room to improve'}
                  </div>
                </div>
                <div className="text-center p-4 bg-gray-900/50 rounded-lg">
                  <div className="text-3xl font-bold text-white">
                    {totalAssets > 0 ? ((netWorth / totalAssets) * 100).toFixed(0) : 0}%
                  </div>
                  <div className="text-gray-400 text-sm mt-1">{t('netWorthChart.equityPercentage')}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t('netWorthChart.portionOwned')}
                  </div>
                </div>
                <div className="text-center p-4 bg-gray-900/50 rounded-lg">
                  <div className={`text-3xl font-bold ${netWorth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {netWorth >= 0 ? t('netWorthChart.positive') : t('netWorthChart.negative')}
                  </div>
                  <div className="text-gray-400 text-sm mt-1">{t('netWorthChart.netWorthStatus')}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {netWorth >= 0 ? t('netWorthChart.buildingWealth') : t('netWorthChart.focusOnDebtReduction')}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assets Breakdown Tab */}
        <TabsContent value="assets" className="space-y-6">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">{t('netWorthChart.assetsBreakdown')}</CardTitle>
              <CardDescription className="text-gray-400">
                {t('netWorthChart.allAssetsFromMyNumbers')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {myNumbers?.assets && Object.entries(myNumbers.assets).map(([key, value]) => {
                  const percentage = totalAssets > 0 ? (value / totalAssets) * 100 : 0;
                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  return (
                    <div key={key} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-300">{label}</span>
                        <span className="text-emerald-400">{formatAmount(value)} ({percentage.toFixed(1)}%)</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Debts Breakdown Tab */}
        <TabsContent value="debts" className="space-y-6">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Debts Breakdown</CardTitle>
              <CardDescription className="text-gray-400">
                All your debts from My Numbers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {myNumbers?.debts && Object.entries(myNumbers.debts).map(([key, value]) => {
                  const percentage = totalDebt > 0 ? (value / totalDebt) * 100 : 0;
                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  return (
                    <div key={key} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-300">{label}</span>
                        <span className="text-red-400">{formatAmount(value)} ({percentage.toFixed(1)}%)</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                {totalDebt === 0 && (
                  <div className="text-center py-8">
                    <div className="text-emerald-400 text-xl font-semibold">Debt Free!</div>
                    <p className="text-gray-400 mt-2">Congratulations on having no debt</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Payments */}
          {myNumbers?.monthly_payments && Object.values(myNumbers.monthly_payments).some(v => v > 0) && (
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Monthly Debt Payments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(myNumbers.monthly_payments).map(([key, value]) => {
                    if (value === 0) return null;
                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    return (
                      <div key={key} className="flex justify-between items-center p-2 bg-gray-900/50 rounded">
                        <span className="text-gray-300">{label}</span>
                        <span className="text-orange-400 font-medium">{formatAmount(value)}/mo</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Net Worth Over Time</CardTitle>
              <CardDescription className="text-gray-400">
                Track your wealth growth
              </CardDescription>
            </CardHeader>
            <CardContent>
              {monthlyHistory.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={monthlyHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="month" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                      labelStyle={{ color: '#9CA3AF' }}
                      formatter={(value) => formatAmount(value)}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="assets" 
                      name="Assets"
                      stroke="#10B981" 
                      fill="#10B98133"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="liabilities" 
                      name="Debts"
                      stroke="#EF4444" 
                      fill="#EF444433"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="net_worth" 
                      name="Net Worth"
                      stroke="#3B82F6" 
                      fill="#3B82F633"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12">
                  <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No historical data yet</p>
                  <p className="text-gray-500 text-sm mt-1">Your net worth history will appear here over time</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NetWorthDisplayModule;
